import React, { useState } from 'react';
import MainLayout from '../components/layout/MainLayout';
import FacultyList from '../features/faculties/FacultyList';
import FacultyForm from '../features/faculties/FacultyForm';
import { useNotification } from '../context/NotificationContext';
import facultyService from '../services/facultyService';

const Faculties = () => {
  const [showForm, setShowForm] = useState(false);
  const [editingFaculty, setEditingFaculty] = useState(null);
  const [viewingFaculty, setViewingFaculty] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const { showSuccess } = useNotification();

  const handleCreate = () => {
    setEditingFaculty(null);
    setShowForm(true);
  };

  const handleEdit = (faculty) => {
    setEditingFaculty(faculty);
    setShowForm(true);
  };

  const handleView = (faculty) => {
    setViewingFaculty(faculty);
  };

  const handleCloseView = () => {
    setViewingFaculty(null);
  };

  const handleFormSubmit = async (formData) => {
    try {
      setLoading(true);
      let response;

if (editingFaculty) {
       response = await facultyService.updateFaculty(editingFaculty.id, formData);
       if (response.success) {
         showSuccess('Faculty updated successfully!');
       }
     } else {
       response = await facultyService.createFaculty(formData);
       if (response.success) {
         showSuccess('Faculty created successfully!');
       }
     }

     setShowForm(false);
     setEditingFaculty(null);
     setRefreshTrigger(prev => prev + 1);
   } catch (error) {
     console.error('Form submission error:', error);
   } finally {
     setLoading(false);
   }
 };

 const handleFormCancel = () => {
   setShowForm(false);
   setEditingFaculty(null);
 };

 const formatDate = (dateString) => {
   return new Date(dateString).toLocaleDateString('en-US', {
     year: 'numeric',
     month: 'long',
     day: 'numeric',
     hour: '2-digit',
     minute: '2-digit'
   });
 };

 return (
   <MainLayout>
     <div className="faculties-page">
       {/* Page Header */}
       <div className="d-flex justify-content-between align-items-center mb-4">
         <div>
           <h1 className="h3 mb-1 fw-bold text-dark">Faculty Management</h1>
           <p className="text-muted mb-0">Manage faculties within campus institutions</p>
         </div>
         <button 
           className="btn btn-primary"
           onClick={handleCreate}
         >
           <i className="fas fa-plus me-2"></i>
           Add Faculty
         </button>
       </div>

       {/* Form Modal */}
       {showForm && (
         <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
           <div className="modal-dialog modal-lg">
             <div className="modal-content">
               <div className="modal-header">
                 <h5 className="modal-title">
                   <i className={`fas ${editingFaculty ? 'fa-edit' : 'fa-plus'} me-2`}></i>
                   {editingFaculty ? 'Edit Faculty' : 'Add New Faculty'}
                 </h5>
                 <button
                   type="button"
                   className="btn-close"
                   onClick={handleFormCancel}
                   disabled={loading}
                 ></button>
               </div>
               <div className="modal-body">
                 <FacultyForm
                   faculty={editingFaculty}
                   onSubmit={handleFormSubmit}
                   onCancel={handleFormCancel}
                   loading={loading}
                 />
               </div>
             </div>
           </div>
         </div>
       )}

       {/* View Modal */}
       {viewingFaculty && (
         <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
           <div className="modal-dialog modal-xl">
             <div className="modal-content">
               <div className="modal-header">
                 <h5 className="modal-title">
                   <i className="fas fa-graduation-cap me-2"></i>
                   Faculty Details
                 </h5>
                 <button
                   type="button"
                   className="btn-close"
                   onClick={handleCloseView}
                 ></button>
               </div>
               <div className="modal-body">
                 <div className="row">
                   <div className="col-md-8">
                     {/* Faculty Header */}
                     <div className="d-flex align-items-center mb-4">
                       <div 
                         className={`faculty-icon rounded-circle d-flex align-items-center justify-content-center me-3 ${
                           viewingFaculty.status ? 'bg-success' : 'bg-secondary'
                         }`}
                         style={{ width: '60px', height: '60px' }}
                       >
                         <i className="fas fa-graduation-cap text-white fs-4"></i>
                       </div>
                       <div>
                         <h4 className="fw-bold text-dark mb-0">{viewingFaculty.nameEn}</h4>
                         <p className="text-muted mb-1">{viewingFaculty.nameLocal}</p>
                         <div className="d-flex gap-2">
                           <span className="badge bg-primary">
                             <i className="fas fa-building me-1"></i>
                             {viewingFaculty.campusName}
                           </span>
                           <span 
                             className={`badge ${viewingFaculty.status ? 'bg-success' : 'bg-danger'}`}
                           >
                             <i className={`fas ${viewingFaculty.status ? 'fa-check-circle' : 'fa-times-circle'} me-1`}></i>
                             {viewingFaculty.status ? 'Active' : 'Inactive'}
                           </span>
                         </div>
                       </div>
                     </div>
                     
                     {/* Basic Information */}
                     <div className="row mb-4">
                       <div className="col-md-6">
                         <label className="form-label fw-bold text-muted small">FACULTY ID</label>
                         <p className="text-dark fs-5">{viewingFaculty.id}</p>
                       </div>
                       <div className="col-md-6">
                         <label className="form-label fw-bold text-muted small">CAMPUS ID</label>
                         <p className="text-dark fs-5">{viewingFaculty.campusId}</p>
                       </div>
                     </div>

                     {/* Names */}
                     <div className="row mb-4">
                       <div className="col-md-6">
                         <label className="form-label fw-bold text-muted small">ENGLISH NAME</label>
                         <p className="text-dark">{viewingFaculty.nameEn}</p>
                       </div>
                       <div className="col-md-6">
                         <label className="form-label fw-bold text-muted small">LOCAL NAME</label>
                         <p className="text-dark">{viewingFaculty.nameLocal}</p>
                       </div>
                     </div>

                     {/* Status & Campus */}
                     <div className="row mb-4">
                       <div className="col-md-6">
                         <label className="form-label fw-bold text-muted small">STATUS</label>
                         <p className="text-dark">
                           <span 
                             className={`badge ${viewingFaculty.status ? 'bg-success' : 'bg-danger'} fs-6`}
                           >
                             {viewingFaculty.status ? 'Active Faculty' : 'Inactive Faculty'}
                           </span>
                         </p>
                       </div>
                       <div className="col-md-6">
                         <label className="form-label fw-bold text-muted small">CAMPUS</label>
                         <p className="text-dark">{viewingFaculty.campusName}</p>
                       </div>
                     </div>

                     {/* Timestamps */}
                     <div className="row">
                       <div className="col-md-6">
                         <label className="form-label fw-bold text-muted small">CREATED</label>
                         <p className="text-dark">{formatDate(viewingFaculty.createdAt)}</p>
                       </div>
                       <div className="col-md-6">
                         <label className="form-label fw-bold text-muted small">LAST UPDATED</label>
                         <p className="text-dark">{formatDate(viewingFaculty.updatedAt)}</p>
                       </div>
                     </div>
                   </div>
                   
                   {/* Statistics Panel */}
                   <div className="col-md-4">
                     <div className="card border-0 bg-light h-100">
                       <div className="card-body">
                         <h6 className="fw-bold mb-4">Faculty Statistics</h6>
                         
                         {/* Students */}
                         <div className="d-flex align-items-center mb-4">
                           <div className="bg-primary rounded-circle d-flex align-items-center justify-content-center me-3"
                                style={{ width: '50px', height: '50px' }}>
                             <i className="fas fa-user-graduate text-white fs-4"></i>
                           </div>
                           <div>
                             <div className="fw-bold fs-3">{viewingFaculty.studentCount || 0}</div>
                             <small className="text-muted">Students Enrolled</small>
                           </div>
                         </div>

                         {/* Departments */}
                         <div className="d-flex align-items-center mb-4">
                           <div className="bg-info rounded-circle d-flex align-items-center justify-content-center me-3"
                                style={{ width: '50px', height: '50px' }}>
                             <i className="fas fa-sitemap text-white fs-4"></i>
                           </div>
                           <div>
                             <div className="fw-bold fs-3">{viewingFaculty.departmentCount || 0}</div>
                             <small className="text-muted">Departments</small>
                           </div>
                         </div>

                         {/* Status Indicator */}
                         <div className="border-top pt-3">
                           <h6 className="fw-bold mb-3">Faculty Status</h6>
                           <div className="d-flex align-items-center">
                             <div 
                               className={`${viewingFaculty.status ? 'bg-success' : 'bg-danger'} rounded-circle d-flex align-items-center justify-content-center me-3`}
                               style={{ width: '30px', height: '30px' }}
                             >
                               <i className={`fas ${viewingFaculty.status ? 'fa-check' : 'fa-times'} text-white`}></i>
                             </div>
                             <span className="fw-medium">
                               {viewingFaculty.status ? 'Currently Active' : 'Currently Inactive'}
                             </span>
                           </div>
                           {!viewingFaculty.status && (
                             <small className="text-muted d-block mt-2">
                               This faculty is not accepting new enrollments
                             </small>
                           )}
                         </div>
                       </div>
                     </div>
                   </div>
                 </div>
               </div>
               <div className="modal-footer">
                 <button
                   type="button"
                   className="btn btn-outline-primary"
                   onClick={() => {
                     handleCloseView();
                     handleEdit(viewingFaculty);
                   }}
                 >
                   <i className="fas fa-edit me-2"></i>
                   Edit Faculty
                 </button>
                 <button
                   type="button"
                   className="btn btn-secondary"
                   onClick={handleCloseView}
                 >
                   Close
                 </button>
               </div>
             </div>
           </div>
         </div>
       )}

       {/* Faculties List */}
       <FacultyList
         onEdit={handleEdit}
         onView={handleView}
         refreshTrigger={refreshTrigger}
       />
     </div>
   </MainLayout>
 );
};

export default Faculties;